// =============================================================================
// CONNECT 2.0 — Dashboard KPI tile counts (Spec 10, Task 1).
// -----------------------------------------------------------------------------
// Pure counting helpers over the same flat `/project/user-projects` shape
// (`Project { sites: SiteDetail[] }`, `SiteDetail { site, proposal, siteVisit }`)
// that list-grouping.util transforms for the list pages. Each SiteDetail is one
// product/proposal line-item, so a tile count is a straight filter+length over
// the flat sites array — no grouping needed for a tally. Pure functions, no
// Angular dependency, so DashboardComponent stays a thin subscribe-and-render
// shell and the counting rules are unit-testable on their own.
// =============================================================================

import { SITE_VISIT_SCHEDULED_STATUSES } from '../list/product-line/product-progress.util';
import type { SiteVisitStatus } from '../site-visit/site-visit-state.util';

export interface DashboardKpis {
  openInvitations: number;
  upcomingSiteVisits: number;
  atpsToSign: number;
}

// Same statuses invitations-page.component.ts filters `sites` on (Task B4
// added COUNTERED — a countered proposal is still open/unresolved; the
// staffing counter-back adds RE_OFFERED — staffing answered the counter with
// a revised fee and the ball is back with the PA, so it is open and
// actionable in exactly the way OFFERED is).
const INVITATION_STATUSES = new Set([
  'INVITATION',
  'OFFERED',
  'COUNTERED',
  'RE_OFFERED',
]);

// Same statuses active-page.component.ts filters `sites` on — a product has to
// be in one of these before a site visit is even schedulable.
const ACTIVE_STATUSES = new Set([
  'ACTIVE',
  'REVIEW_PENDING',
  'REVISION_REQUESTED',
  'REVISION_PENDING',
]);

/** Every flat SiteDetail row across every project, tolerant of holes. */
function allSiteDetails(projects: any[]): any[] {
  return (Array.isArray(projects) ? projects : [])
    .flatMap((project) => (Array.isArray(project?.sites) ? project.sites : []))
    .filter((detail: any) => !!detail);
}

/**
 * A product counts as an "upcoming site visit" when it is an active-page product
 * whose `SiteVisitSchedule` summary (`SiteDetail.siteVisit`, from
 * `GET /project/user-projects`) says a day is on the books that hasn't happened
 * yet:
 *  - the status has reached {@link SITE_VISIT_SCHEDULED_STATUSES} (an agreed
 *    day exists — same bar `product-progress.util`'s `scheduleSiteVisit` stage
 *    uses), AND
 *  - it is not yet `SITE_VISIT_COMPLETED` (that set also contains the terminal
 *    completed status — a finished visit is not "upcoming"), AND
 *  - `confirmedDate` has not passed.
 *
 * Replaces two dead reads: `proposal.selectedSiteVisit` is written only by the
 * Order Tracker staff flow (never by CONNECT 2.0's own negotiation), and
 * `siteVisit1/2/3` are the retired vendor-submitted preferred-window fields.
 * Both read 0 for every visit CONNECT 2.0 itself schedules.
 *
 * `site.siteVisitRequired` gates the whole predicate. A visit on a product that
 * does not require one is not an "upcoming site visit", because nothing
 * site-visit-related is surfaced for such a product at all — not the stepper's
 * two stages, not the workspace Details row, not the field-work checklist row,
 * and not this tile. The gate is load-bearing, not decorative: visit creation is
 * not gated on `Site.siteVisitRequired` anywhere in connect-service, so a stray
 * schedule on a site flagged false is a reachable shape, and without the gate it
 * would be counted here.
 *
 * This is the rule `scheduling-card.component.ts` has always applied (see its
 * `requiringProducts`/`requiringCount` filter on `site.siteVisitRequired`).
 * `product-progress.util`'s `visitRequired` and the two workspace surfaces were
 * brought onto the same flag, so every surface now answers the same way.
 */
function hasUpcomingSiteVisit(detail: any): boolean {
  const proposal = detail?.proposal ?? {};
  if (detail?.site?.siteVisitRequired !== true) {
    return false;
  }
  if (!ACTIVE_STATUSES.has(proposal.status)) {
    return false;
  }
  const siteVisit = detail?.siteVisit;
  const status = siteVisit?.status == null ? null : String(siteVisit.status).toUpperCase();
  if (
    status === null ||
    !SITE_VISIT_SCHEDULED_STATUSES.has(status as SiteVisitStatus) ||
    status === 'SITE_VISIT_COMPLETED'
  ) {
    return false;
  }
  // 🔴 `confirmedDate`, not `approvedDate` — the summary follows `SiteVisitSchedule`'s rename, and
  // `detail` is `any`, so the old key read `undefined` here and this tile silently counted nothing.
  const confirmedDate = siteVisit?.confirmedDate ? new Date(siteVisit.confirmedDate) : null;
  return (
    !!confirmedDate && !isNaN(confirmedDate.getTime()) && confirmedDate.getTime() >= Date.now()
  );
}

/** Compute the 3 live dashboard KPI counts from the raw project-details feed. */
export function computeDashboardKpis(projects: any[]): DashboardKpis {
  const details = allSiteDetails(projects);
  return {
    openInvitations: details.filter((detail) =>
      INVITATION_STATUSES.has(detail?.proposal?.status)
    ).length,
    upcomingSiteVisits: details.filter(hasUpcomingSiteVisit).length,
    atpsToSign: details.filter(
      (detail) => detail?.proposal?.status === 'BID_WON'
    ).length,
  };
}
